import { HOVER_HINT_PANEL_SELECTOR } from "./viewportAnchoredPopover";

export { HOVER_HINT_PANEL_SELECTOR };

/** Hover/focus wrap around the hint trigger. */
export const HOVER_HINT_ANCHOR_SELECTOR = "[data-hub-hover-hint-anchor]";

/** Shared by a parent hint and nested hints portaled from inside it. */
export const HOVER_HINT_ROOT_ATTR = "data-hub-hover-hint-root";

/**
 * Pointer travel shorter than this is treated as stopped (or jitter), not a new aim.
 * The hint then closes after {@link HOVER_HINT_APPROACH_IDLE_MS} if no further movement arrives.
 */
export const HOVER_HINT_APPROACH_MIN_TRAVEL_PX = 2;

/**
 * How long the pointer may sit still in the gap before the hint closes.
 * This is only the stop detector. Movement away from both the trigger and the panel closes
 * on that pointer event, with no hide delay.
 */
export const HOVER_HINT_APPROACH_IDLE_MS = 80;

/** Extra pixels around the target box so a path that clips a corner still counts as approach. */
export const HOVER_HINT_APPROACH_SLOP_PX = 8;

export type HoverHintPoint = { x: number; y: number };

export type HoverHintRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

/** `toward` keeps the hint open, `away` closes now, `still` waits for the idle detector. */
export type HoverHintPointerAim = "toward" | "away" | "still";

function eventElement(node: EventTarget | null): Element | null {
  if (node instanceof Element) return node;
  if (node instanceof Node) return node.parentElement;
  return null;
}

export function hoverHintRootIdFromNode(node: EventTarget | null): string | null {
  const element = eventElement(node);
  if (element == null) return null;
  const panel = element.closest(HOVER_HINT_PANEL_SELECTOR);
  if (panel == null) return null;
  const root = panel.getAttribute(HOVER_HINT_ROOT_ATTR);
  return root != null && root !== "" ? root : null;
}

export function shouldKeepHoverHintOpen(args: {
  isInsideAnchor: boolean;
  isInsideThisHintTree: boolean;
}): boolean {
  return args.isInsideAnchor || args.isInsideThisHintTree;
}

export function isNodeInsideHoverHintAnchor(
  node: EventTarget | null,
  anchor: { contains: (other: Node) => boolean } | null,
): boolean {
  if (anchor == null || !(node instanceof Node)) {
    return false;
  }
  return anchor.contains(node);
}

export function isNodeInsideThisHintTree(node: EventTarget | null, rootId: string): boolean {
  if (rootId === "") return false;
  return hoverHintRootIdFromNode(node) === rootId;
}

/** Pointer is over a different hover hint (another nav row, another ? glyph, etc.). */
export function isNodeInsideForeignHoverHint(
  node: EventTarget | null,
  anchor: { contains: (other: Node) => boolean } | null,
  rootId: string,
): boolean {
  if (isNodeInsideHoverHintAnchor(node, anchor)) return false;
  if (isNodeInsideThisHintTree(node, rootId)) return false;
  const element = eventElement(node);
  if (element == null) return false;
  if (element.closest(HOVER_HINT_ANCHOR_SELECTOR) != null) return true;
  return hoverHintRootIdFromNode(node) != null;
}

function expandRect(rect: HoverHintRect, slop: number): HoverHintRect {
  return {
    left: rect.left - slop,
    top: rect.top - slop,
    right: rect.right + slop,
    bottom: rect.bottom + slop,
  };
}

function pointInsideRect(point: HoverHintPoint, rect: HoverHintRect): boolean {
  return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
}

/** Positive when `b` is counter-clockwise from `a` around `origin`. */
function cross(origin: HoverHintPoint, a: HoverHintPoint, b: HoverHintPoint): number {
  return (a.x - origin.x) * (b.y - origin.y) - (a.y - origin.y) * (b.x - origin.x);
}

function rectCorners(rect: HoverHintRect): HoverHintPoint[] {
  return [
    { x: rect.left, y: rect.top },
    { x: rect.right, y: rect.top },
    { x: rect.right, y: rect.bottom },
    { x: rect.left, y: rect.bottom },
  ];
}

/**
 * Safe-triangle aim: the step from `previous` to `current` stays inside the cone from
 * `previous` to the target box and moves closer to that box (Amazon mega-menu approach).
 */
export function isPointerHeadingTowardRect(
  previous: HoverHintPoint,
  current: HoverHintPoint,
  rect: HoverHintRect,
): boolean {
  const target = expandRect(rect, HOVER_HINT_APPROACH_SLOP_PX);
  if (pointInsideRect(previous, target)) return pointInsideRect(current, target);
  if (pointInsideRect(current, target)) return true;

  const dx = current.x - previous.x;
  const dy = current.y - previous.y;
  if (dx * dx + dy * dy < HOVER_HINT_APPROACH_MIN_TRAVEL_PX * HOVER_HINT_APPROACH_MIN_TRAVEL_PX) {
    return false;
  }

  const centerX = (target.left + target.right) / 2;
  const centerY = (target.top + target.bottom) / 2;
  if (dx * (centerX - previous.x) + dy * (centerY - previous.y) <= 0) return false;

  const corners = rectCorners(target);
  let counterClockwise = corners[0];
  let clockwise = corners[0];
  if (counterClockwise == null || clockwise == null) return false;
  for (const corner of corners) {
    if (cross(previous, counterClockwise, corner) > 0) counterClockwise = corner;
    if (cross(previous, clockwise, corner) < 0) clockwise = corner;
  }
  if (cross(previous, clockwise, current) < 0) return false;
  if (cross(previous, counterClockwise, current) > 0) return false;
  return true;
}

export function hoverHintPointerAim(
  previous: HoverHintPoint,
  current: HoverHintPoint,
  rects: readonly HoverHintRect[],
): HoverHintPointerAim {
  const dx = current.x - previous.x;
  const dy = current.y - previous.y;
  if (dx * dx + dy * dy < HOVER_HINT_APPROACH_MIN_TRAVEL_PX * HOVER_HINT_APPROACH_MIN_TRAVEL_PX) {
    return "still";
  }
  if (rects.length === 0) return "still";
  for (const rect of rects) {
    if (isPointerHeadingTowardRect(previous, current, rect)) return "toward";
  }
  return "away";
}

export function isNodeInsideHoverHintSurface(
  node: EventTarget | null,
  anchor: { contains: (other: Node) => boolean } | null,
  rootId: string,
): boolean {
  return shouldKeepHoverHintOpen({
    isInsideAnchor: isNodeInsideHoverHintAnchor(node, anchor),
    isInsideThisHintTree: isNodeInsideThisHintTree(node, rootId),
  });
}
